import { type ReactElement, type ReactNode, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';

import { SheetRoot, SheetTrigger } from './Sheet.tsx';

/** Which openers are on the page (mounted), by id; the open sheet `Rest` is drawing; who to tell. */
interface Store {
	mounted: Map<string, number>;
	restHolds: string | null;
	subs: Set<() => void>;
	subscribe: (f: () => void) => () => void;
}

function newStore(): Store {
	const subs = new Set<() => void>();
	return {
		mounted: new Map(),
		restHolds: null,
		subs,
		subscribe: (f) => {
			subs.add(f);
			return () => {
				subs.delete(f);
			};
		},
	};
}

const tell = (store: Store) => {
	for (const f of store.subs) f();
};

/** One opener: its sheet's root, its button as the trigger, and the sheet while it's the one open. */
function Opener({ store, id, open, sheet, children }: { store: Store; id: string; open: boolean; sheet: ReactNode; children: ReactElement }) {
	// On the page before this render: an opener that arrives with its sheet already open (the
	// page changed as it opened: Start → Invite) leaves it to Rest, which counts only these.
	const here = useRef(false);
	useLayoutEffect(() => {
		here.current = true;
		store.mounted.set(id, (store.mounted.get(id) ?? 0) + 1);
		tell(store);
		return () => {
			const n = (store.mounted.get(id) ?? 1) - 1;
			if (n > 0) store.mounted.set(id, n);
			else store.mounted.delete(id);
			here.current = false;
			tell(store);
		};
	}, [store, id]);
	// Not if Rest drew it first (it opened before this button was on the page): no remount mid-sheet.
	const restHolds = useSyncExternalStore(store.subscribe, () => store.restHolds, () => null);
	const draws = open && here.current && restHolds !== id;
	return (
		<SheetRoot open={open}>
			<SheetTrigger>{children}</SheetTrigger>
			{draws ? sheet : null}
		</SheetRoot>
	);
}

/**
 * The open sheet, when no opener of its was on the page as it opened (opened from code, or
 * from a row in another sheet), or its opener has left the page. Once it's drawing a sheet it
 * keeps it until that sheet closes, even if its button turns up meanwhile.
 */
function Rest({ store, current, sheet }: { store: Store; current: string | null; sheet: ReactNode }) {
	const mounted = useSyncExternalStore(store.subscribe, () => current !== null && store.mounted.has(current), () => false);
	const [hold, setHold] = useState<{ for: string | null; rest: boolean }>({ for: null, rest: false });
	let rest = hold.rest;
	if (hold.for !== current || (!rest && current !== null && !mounted)) {
		rest = current !== null && !mounted;
		if (hold.for !== current || hold.rest !== rest) setHold({ for: current, rest });
	}
	const holds = rest ? current : null;
	useLayoutEffect(() => {
		if (store.restHolds === holds) return;
		store.restHolds = holds;
		tell(store);
	}, [store, holds]);
	return rest ? sheet : null;
}

/**
 * One sheet at a time with several buttons that open it, some of which may be off the page
 * (Play's builder: the New collection tile, the rail's New collection, Save as a collection,
 * Edit; Nest's sheets). `current` is the id of the opener the open sheet came from (null:
 * none open); `sheet()` draws the open one.
 *
 * `trigger(id, button)` wraps a button in the sheet's Radix root and trigger (`SheetRoot`,
 * `SheetTrigger`), so it says it opens a dialog and whether it's open, and focus goes back
 * to it; while `current` is its id, the sheet is drawn inside its root. `rest` draws the sheet
 * when no opener with that id is on the page (opened from code, or by a row in another
 * sheet): put it where the sheet used to be. Give each button on the page its own id.
 */
export function useSheetOpeners(current: string | null, sheet: () => ReactNode) {
	const [store] = useState<Store>(newStore);
	return {
		trigger: (id: string, button: ReactElement) => (
			<Opener store={store} id={id} open={current === id} sheet={current === id ? sheet() : null}>
				{button}
			</Opener>
		),
		rest: <Rest store={store} current={current} sheet={current === null ? null : sheet()} />,
	};
}
